import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_BASE_URL } from '../config/api';

export interface User {
  id: string;
  display_name: string;
  created_at: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface ScoreSubmission {
  score: number;
  game_duration: number;
}

export interface LeaderboardEntry {
  user_id: string;
  display_name: string;
  score: number;
  created_at: string;
  rank?: number;
}

export interface LeaderboardResponse {
  entries: LeaderboardEntry[];
  total_count: number;
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers) => {
      try {
        const token = await AsyncStorage.getItem('auth_token');
        if (token) {
          headers.set('authorization', `Bearer ${token}`);
        }
      } catch (error) {
        console.warn('AsyncStorage not available:', error);
      }
      return headers;
    },
  }),
  tagTypes: ['Score', 'Leaderboard', 'User'],
  endpoints: (builder) => ({
    // 认证相关
    googleAuth: builder.mutation<AuthResponse, { access_token: string }>({
      query: (credentials) => ({
        url: '/auth/google',
        method: 'POST',
        body: credentials,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          try {
            await AsyncStorage.setItem('auth_token', data.token);
          } catch (storageError) {
            console.warn('AsyncStorage not available for saving token:', storageError);
          }
        } catch (error) {
          console.error('Failed to save token:', error);
        }
      },
    }),

    appleAuth: builder.mutation<AuthResponse, { identity_token: string }>({
      query: (credentials) => ({
        url: '/auth/apple',
        method: 'POST',
        body: credentials,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          try {
            await AsyncStorage.setItem('auth_token', data.token);
          } catch (storageError) {
            console.warn('AsyncStorage not available for saving token:', storageError);
          }
        } catch (error) {
          console.error('Failed to save token:', error);
        }
      },
    }),

    // 分数相关
    submitScore: builder.mutation<any, ScoreSubmission>({
      query: (scoreData) => ({
        url: '/scores',
        method: 'POST',
        body: scoreData,
      }),
      invalidatesTags: ['Score', 'Leaderboard'],
    }),

    // 排行榜相关
    getLeaderboard: builder.query<LeaderboardResponse, { limit?: number; offset?: number }>({
      query: ({ limit = 10, offset = 0 } = {}) =>
        `/leaderboard?limit=${limit}&offset=${offset}`,
      providesTags: ['Leaderboard'],
    }),

    getUserRank: builder.query<LeaderboardEntry, void>({
      query: () => '/leaderboard/me',
      providesTags: ['User'],
    }),
  }),
});

export const {
  useGoogleAuthMutation,
  useAppleAuthMutation,
  useSubmitScoreMutation,
  useGetLeaderboardQuery,
  useGetUserRankQuery,
} = api;
